// Descriptor-driven config for the Inventory attribute tables.
//
// Each product line that gets its own attribute columns (Gutter Coil, Downspouts,
// Elbows, Miters, End Caps, Gutter Guards, insulation batting) is ONE entry here.
// InventoryPage's `InventoryTable`/`InventoryRow` render the colgroup, header row,
// body cells, colCount, sort accessor, and table min-w by mapping over the active
// entry's `cols` — instead of 7+ hand-written branches at ~10 sites each.
//
// BEHAVIOR-NEUTRAL CONTRACT: every value here is transcribed VERBATIM from the
// former inline branches (exact `w-[…]`, label, sortKey, className, `tnum`, and
// min-w). The per-cell `tnum` and `px-2`-vs-`px-4` choices are IRREGULAR (e.g.
// elbow Type is tnum; coil Color is not; insulation uses px-4) — they are copied,
// not derived. The standard (Name) table + the editable numeric cells (On hand /
// Committed / Free / Reorder / Cost / Price) are NOT modeled here; they stay
// literal in InventoryPage.

import type { ReactNode } from 'react';
import type { InventorySkuRow } from '@gutterinv/shared';
import {
  coilSizeSortKey,
  coilWidthSortKey,
  coilThicknessSortKey,
  downspoutConnectorSizeSortKey,
  downspoutLengthSortKey,
  elbowConnectorSizeSortKey,
  elbowAngleSortKey,
  elbowOffsetSortKey,
  miterSizeSortKey,
  endcapSizeSortKey,
  guardWidthSortKey,
  outletSizeSortKey,
  adapterDimSortKey,
  hangerSizeSortKey,
  wedgeShankSizeSortKey,
  fastenerGaugeSortKey,
  fastenerLengthSortKey,
  sealerPaintSizeSortKey,
  toolSizeSortKey,
  normalizeUom,
} from '@gutterinv/shared';
import { rValueSortKey } from '../lib/insulation-sort.js';
import {
  isBattingCategory,
  isGutterCoilCategory,
  isDownspoutCategory,
  isElbowCategory,
  isMiterCategory,
  isEndCapCategory,
  isGutterGuardCategory,
  isOutletCategory,
  isAdapterConductorCategory,
  isHangerCategory,
  isWedgeShankCategory,
  isFastenerCategory,
  isSealerPaintCategory,
  isToolCategory,
} from '../lib/product-line.js';

// Sortable column keys. COMMON keys (code/name/onHand/committed/free/reorder/cost)
// are handled by InventoryPage's own switch; the rest are the attribute columns
// below. Each `cols[].sortKey` is typed as InvSortKey so a typo fails tsc.
export type InvSortKey =
  | 'code'
  | 'name'
  | 'onHand'
  | 'committed'
  | 'free'
  | 'reorder'
  | 'cost'
  | 'rValue'
  | 'face'
  | 'widthIn'
  | 'coilSize'
  | 'coilStyle'
  | 'coilColor'
  | 'coilWidth'
  | 'coilThickness'
  | 'coilMaterial'
  | 'downspoutStyle'
  | 'downspoutConnectorSize'
  | 'downspoutLength'
  | 'downspoutMaterial'
  | 'downspoutColor'
  | 'elbowStyle'
  | 'elbowConnectorSize'
  | 'elbowType'
  | 'elbowAngle'
  | 'elbowOffset'
  | 'elbowMaterial'
  | 'elbowColor'
  | 'miterStyle'
  | 'miterType'
  | 'miterPosition'
  | 'miterSize'
  | 'miterMaterial'
  | 'miterColor'
  | 'endcapStyle'
  | 'endcapSize'
  | 'endcapSide'
  | 'endcapMaterial'
  | 'endcapColor'
  | 'guardBrand'
  | 'guardType'
  | 'guardWidth'
  | 'guardLength'
  | 'guardMaterial'
  | 'guardColor'
  | 'outletBrand'
  | 'outletType'
  | 'outletSize'
  | 'outletMaterial'
  | 'outletColor'
  | 'adapterBrand'
  | 'adapterType'
  | 'adapterStyle'
  | 'adapterDim1'
  | 'adapterDim2'
  | 'adapterMaterial'
  | 'adapterColor'
  | 'hangerBrand'
  | 'hangerType'
  | 'hangerStyle'
  | 'hangerSize'
  | 'hangerMaterial'
  | 'hangerColor'
  | 'wedgeShankBrand'
  | 'wedgeShankType'
  | 'wedgeShankSize'
  | 'wedgeShankMaterial'
  | 'wedgeShankColor'
  | 'fastenerType'
  | 'fastenerGauge'
  | 'fastenerLength'
  | 'fastenerColor'
  | 'sealerPaintType'
  | 'sealerPaintBrand'
  | 'sealerPaintSize'
  | 'sealerPaintColor'
  | 'toolType'
  | 'toolSize';

export interface AttrColDescriptor {
  /** Header label text (also the React key within a table — labels are unique per table). */
  label: string;
  /** colgroup width class, or null for the width-less slack col (insulation SF/bag). */
  colClass: string | null;
  /** className passed to <SortHeader> (sortable) OR the full <th> class (non-sortable). */
  headerClass: string;
  /** false => non-sortable plain <th> with an eyebrow span (insulation Length / SF-bag). */
  sortable?: boolean;
  /** Body <td> className (verbatim — encodes the per-cell tnum + px-2/px-4 choice). */
  cellClass: string;
  /** Body value renderer (raw field ?? '—', or a transform for insulation dims). */
  render: (s: InventorySkuRow) => ReactNode;
  /** Sort key + accessor — present ONLY for sortable cols (omitted for the
   *  non-sortable insulation Length / SF-bag). sortKey typed InvSortKey so a typo
   *  fails tsc; the accessor feeds InventoryPage's invAccessor map. */
  sortKey?: InvSortKey;
  sortAccessor?: (s: InventorySkuRow) => string | number | null;
  /** The leading attribute cell (bold span + Disabled badge). */
  isFirstCell?: boolean;
}

export interface AttributeTableDescriptor {
  categoryFilter: (name: string) => boolean;
  cols: AttrColDescriptor[];
  /** Table min-w class WITH the Cost column (owner/admin). Tuned constant. */
  minWWithCost: string;
  /** Table min-w class WITHOUT Cost. Tuned constant. */
  minWNoCost: string;
  /** Per-table SKU column width (sized to this category's longest code). When set,
   *  InventoryPage uses it instead of the shared 11rem SKU col. */
  skuWidth?: string;
  /** Cross-table ALIGNED tables (gutter components): the SKU→On Hand span is a common
   *  width (so On Hand…Price line up across them) and attribute cols carry equal gaps.
   *  These render WITHOUT w-full (content-tight, no proportional stretch). */
  align?: boolean;
}

const GH = 'pl-2 pr-2 whitespace-nowrap'; // gutter header
const GC = 'px-2 py-3 text-left text-sm text-muted-foreground whitespace-nowrap'; // gutter cell
const GCT = 'tnum px-2 py-3 text-left text-sm text-muted-foreground whitespace-nowrap'; // gutter cell, tnum
const GF = 'px-2 py-3 text-left'; // gutter first cell
const IH = 'whitespace-nowrap'; // insulation sortable header
const ITH = 'px-4 py-3 text-left whitespace-nowrap'; // insulation non-sortable <th>
const IF = 'px-4 py-3 text-left'; // insulation first cell
const IC = 'px-4 py-3 text-left text-sm text-muted-foreground whitespace-nowrap'; // insulation cell
const ICT = 'tnum px-4 py-3 text-left text-sm text-muted-foreground whitespace-nowrap'; // insulation cell, tnum
const dash = (v: string | number | null | undefined): ReactNode => v ?? '—';

export const ATTRIBUTE_TABLES = {
  insulation: {
    categoryFilter: isBattingCategory,
    // Face holds the spelled-out facing ("FSK (Foil-Scrim-Kraft) Faced", 28 chars) → w-64.
    // Width/Length are w-24 (not w-20) to fit fractional widths (e.g. 23.625") and decimal
    // roll lengths in inches (469.99") — proven by the inventory-column-fit guard. Σ fixed
    // grew by 11rem (Face) + 2rem (Width+Length) over the old 79/71 → 92/84.
    minWWithCost: 'min-w-[92rem]',
    minWNoCost: 'min-w-[84rem]',
    cols: [
      { label: 'R-Value', colClass: 'w-24', headerClass: IH, cellClass: IF, isFirstCell: true,
        sortKey: 'rValue', sortAccessor: (s) => rValueSortKey(s.rValue), render: (s) => dash(s.rValue) },
      { label: 'Face', colClass: 'w-64', headerClass: IH, cellClass: IC,
        sortKey: 'face', sortAccessor: (s) => s.face, render: (s) => dash(s.face) },
      { label: 'Width', colClass: 'w-24', headerClass: IH, cellClass: ICT,
        sortKey: 'widthIn', sortAccessor: (s) => s.widthIn,
        render: (s) => (s.widthIn != null ? `${s.widthIn}"` : '—') },
      { label: 'Length', colClass: 'w-24', headerClass: ITH, sortable: false, cellClass: ICT,
        render: (s) => (s.lengthIn != null ? `${s.lengthIn}"` : '—') },
      { label: 'SF/bag', colClass: null, headerClass: ITH, sortable: false, cellClass: ICT,
        render: (s) => (s.coverageSf != null ? s.coverageSf.toLocaleString() : '—') },
    ],
  },
  gutterCoil: {
    categoryFilter: isGutterCoilCategory,
    skuWidth: 'w-[12.25rem]',
    align: true,
    minWWithCost: 'min-w-[95.75rem]',
    minWNoCost: 'min-w-[88.75rem]',
    cols: [
      { label: 'Size', colClass: 'w-[4.781rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'coilSize', sortAccessor: (s) => coilSizeSortKey(s.coilSize), render: (s) => dash(s.coilSize) },
      { label: 'Style', colClass: 'w-[6.406rem]', headerClass: GH, cellClass: GC,
        sortKey: 'coilStyle', sortAccessor: (s) => s.coilStyle, render: (s) => dash(s.coilStyle) },
      { label: 'Color', colClass: 'w-[10.719rem]', headerClass: GH, cellClass: GC,
        sortKey: 'coilColor', sortAccessor: (s) => s.coilColor, render: (s) => dash(s.coilColor) },
      { label: 'Width', colClass: 'w-[5.719rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'coilWidth', sortAccessor: (s) => coilWidthSortKey(s.coilWidth), render: (s) => dash(s.coilWidth) },
      { label: 'Thickness', colClass: 'w-[7.469rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'coilThickness', sortAccessor: (s) => coilThicknessSortKey(s.coilThickness), render: (s) => dash(s.coilThickness) },
      { label: 'Material', colClass: 'w-[7.156rem]', headerClass: GH, cellClass: GC,
        sortKey: 'coilMaterial', sortAccessor: (s) => s.coilMaterial, render: (s) => dash(s.coilMaterial) },
    ],
  },
  downspout: {
    categoryFilter: isDownspoutCategory,
    skuWidth: 'w-[12.75rem]',
    align: true,
    minWWithCost: 'min-w-[95.75rem]',
    minWNoCost: 'min-w-[88.75rem]',
    cols: [
      { label: 'Style', colClass: 'w-[7.062rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'downspoutStyle', sortAccessor: (s) => s.downspoutStyle, render: (s) => dash(s.downspoutStyle) },
      { label: 'Size', colClass: 'w-[6.375rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'downspoutConnectorSize', sortAccessor: (s) => downspoutConnectorSizeSortKey(s.downspoutConnectorSize), render: (s) => dash(s.downspoutConnectorSize) },
      { label: 'Length', colClass: 'w-[6.625rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'downspoutLength', sortAccessor: (s) => downspoutLengthSortKey(s.downspoutLength), render: (s) => dash(s.downspoutLength) },
      { label: 'Material', colClass: 'w-[7.812rem]', headerClass: GH, cellClass: GC,
        sortKey: 'downspoutMaterial', sortAccessor: (s) => s.downspoutMaterial, render: (s) => dash(s.downspoutMaterial) },
      { label: 'Color', colClass: 'w-[13.875rem]', headerClass: GH, cellClass: GC,
        sortKey: 'downspoutColor', sortAccessor: (s) => s.downspoutColor, render: (s) => dash(s.downspoutColor) },
    ],
  },
  elbow: {
    categoryFilter: isElbowCategory,
    skuWidth: 'w-[10.75rem]',
    align: true,
    minWWithCost: 'min-w-[95.75rem]',
    minWNoCost: 'min-w-[88.75rem]',
    cols: [
      { label: 'Style', colClass: 'w-[7.321rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'elbowStyle', sortAccessor: (s) => s.elbowStyle, render: (s) => dash(s.elbowStyle) },
      { label: 'Size', colClass: 'w-[4.071rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'elbowConnectorSize', sortAccessor: (s) => elbowConnectorSizeSortKey(s.elbowConnectorSize), render: (s) => dash(s.elbowConnectorSize) },
      { label: 'Type', colClass: 'w-[3.571rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'elbowType', sortAccessor: (s) => s.elbowType, render: (s) => dash(s.elbowType) },
      { label: 'Angle', colClass: 'w-[4.071rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'elbowAngle', sortAccessor: (s) => elbowAngleSortKey(s.elbowAngle), render: (s) => dash(s.elbowAngle) },
      { label: 'Offset', colClass: 'w-[6.821rem]', headerClass: GH, cellClass: GC,
        sortKey: 'elbowOffset', sortAccessor: (s) => elbowOffsetSortKey(s.elbowOffset), render: (s) => dash(s.elbowOffset) },
      { label: 'Material', colClass: 'w-[6.321rem]', headerClass: GH, cellClass: GC,
        sortKey: 'elbowMaterial', sortAccessor: (s) => s.elbowMaterial, render: (s) => dash(s.elbowMaterial) },
      { label: 'Color', colClass: 'w-[11.571rem]', headerClass: GH, cellClass: GC,
        sortKey: 'elbowColor', sortAccessor: (s) => s.elbowColor, render: (s) => dash(s.elbowColor) },
    ],
  },
  miter: {
    categoryFilter: isMiterCategory,
    skuWidth: 'w-[10.25rem]',
    align: true,
    minWWithCost: 'min-w-[95.75rem]',
    minWNoCost: 'min-w-[88.75rem]',
    cols: [
      { label: 'Style', colClass: 'w-[8.136rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'miterStyle', sortAccessor: (s) => s.miterStyle, render: (s) => dash(s.miterStyle) },
      { label: 'Type', colClass: 'w-[5.636rem]', headerClass: GH, cellClass: GC,
        sortKey: 'miterType', sortAccessor: (s) => s.miterType, render: (s) => dash(s.miterType) },
      { label: 'Position', colClass: 'w-[6.886rem]', headerClass: GH, cellClass: GC,
        sortKey: 'miterPosition', sortAccessor: (s) => s.miterPosition, render: (s) => dash(s.miterPosition) },
      { label: 'Size', colClass: 'w-[4.886rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'miterSize', sortAccessor: (s) => miterSizeSortKey(s.miterSize), render: (s) => dash(s.miterSize) },
      { label: 'Material', colClass: 'w-[7.573rem]', headerClass: GH, cellClass: GC,
        sortKey: 'miterMaterial', sortAccessor: (s) => s.miterMaterial, render: (s) => dash(s.miterMaterial) },
      { label: 'Color', colClass: 'w-[11.136rem]', headerClass: GH, cellClass: GC,
        sortKey: 'miterColor', sortAccessor: (s) => s.miterColor, render: (s) => dash(s.miterColor) },
    ],
  },
  endcap: {
    categoryFilter: isEndCapCategory,
    skuWidth: 'w-[10.25rem]',
    align: true,
    minWWithCost: 'min-w-[95.75rem]',
    minWNoCost: 'min-w-[88.75rem]',
    cols: [
      { label: 'Style', colClass: 'w-[9.263rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'endcapStyle', sortAccessor: (s) => s.endcapStyle, render: (s) => dash(s.endcapStyle) },
      { label: 'Size', colClass: 'w-[6.013rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'endcapSize', sortAccessor: (s) => endcapSizeSortKey(s.endcapSize), render: (s) => dash(s.endcapSize) },
      { label: 'Side', colClass: 'w-[8.013rem]', headerClass: GH, cellClass: GC,
        sortKey: 'endcapSide', sortAccessor: (s) => s.endcapSide, render: (s) => dash(s.endcapSide) },
      { label: 'Material', colClass: 'w-[8.7rem]', headerClass: GH, cellClass: GC,
        sortKey: 'endcapMaterial', sortAccessor: (s) => s.endcapMaterial, render: (s) => dash(s.endcapMaterial) },
      { label: 'Color', colClass: 'w-[12.263rem]', headerClass: GH, cellClass: GC,
        sortKey: 'endcapColor', sortAccessor: (s) => s.endcapColor, render: (s) => dash(s.endcapColor) },
    ],
  },
  guard: {
    categoryFilter: isGutterGuardCategory,
    // Content-sized, NOT cross-aligned: vendor values (Brand "Premier Gutter Cover"=20,
    // Type "Standard Guard"=14, Material "Stainless Steel"=15) make this table wider than
    // the shared P=54.5rem span, so it carries its own min-w instead of the aligned spacer.
    skuWidth: 'w-[13.75rem]',
    minWWithCost: 'min-w-[103.5rem]',
    minWNoCost: 'min-w-[96.5rem]',
    cols: [
      { label: 'Brand', colClass: 'w-[11.75rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'guardBrand', sortAccessor: (s) => s.guardBrand, render: (s) => dash(s.guardBrand) },
      { label: 'Type', colClass: 'w-[8.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'guardType', sortAccessor: (s) => s.guardType, render: (s) => dash(s.guardType) },
      { label: 'Width', colClass: 'w-[4.5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'guardWidth', sortAccessor: (s) => guardWidthSortKey(s.guardWidth), render: (s) => dash(s.guardWidth) },
      { label: 'Length', colClass: 'w-[5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'guardLength', sortAccessor: (s) => s.guardLength, render: (s) => dash(s.guardLength) },
      { label: 'Material', colClass: 'w-[9.25rem]', headerClass: GH, cellClass: GC,
        sortKey: 'guardMaterial', sortAccessor: (s) => s.guardMaterial, render: (s) => dash(s.guardMaterial) },
      { label: 'Color', colClass: 'w-[9.25rem]', headerClass: GH, cellClass: GC,
        sortKey: 'guardColor', sortAccessor: (s) => s.guardColor, render: (s) => dash(s.guardColor) },
    ],
  },
  // Outlets — 4 attrs. Widths PROVISIONAL; tune after a deploy via the
  // getBoundingClientRect measurement (size to the table's own widest value).
  outlet: {
    categoryFilter: isOutletCategory,
    skuWidth: 'w-[11.75rem]',
    minWWithCost: 'min-w-[94.25rem]',
    minWNoCost: 'min-w-[87.25rem]',
    cols: [
      { label: 'Brand', colClass: 'w-[12.25rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'outletBrand', sortAccessor: (s) => s.outletBrand, render: (s) => dash(s.outletBrand) },
      { label: 'Type', colClass: 'w-[8.25rem]', headerClass: GH, cellClass: GC,
        sortKey: 'outletType', sortAccessor: (s) => s.outletType, render: (s) => dash(s.outletType) },
      { label: 'Size', colClass: 'w-[5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'outletSize', sortAccessor: (s) => outletSizeSortKey(s.outletSize), render: (s) => dash(s.outletSize) },
      { label: 'Material', colClass: 'w-[6rem]', headerClass: GH, cellClass: GC,
        sortKey: 'outletMaterial', sortAccessor: (s) => s.outletMaterial, render: (s) => dash(s.outletMaterial) },
      { label: 'Color', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'outletColor', sortAccessor: (s) => s.outletColor, render: (s) => dash(s.outletColor) },
    ],
  },
  // Adapters & Conductors — 6 attrs (Dim1 = inlet, Dim2 = outlet). Widths
  // PROVISIONAL; tune after a deploy. Type ("Drain Tile Adapter") + Style
  // ("Victorian Large") are long → wide table (scrolls).
  adapterConductor: {
    categoryFilter: isAdapterConductorCategory,
    skuWidth: 'w-[10.75rem]',
    minWWithCost: 'min-w-[109.75rem]',
    minWNoCost: 'min-w-[102.75rem]',
    cols: [
      { label: 'Brand', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'adapterBrand', sortAccessor: (s) => s.adapterBrand, render: (s) => dash(s.adapterBrand) },
      { label: 'Type', colClass: 'w-[10.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'adapterType', sortAccessor: (s) => s.adapterType, render: (s) => dash(s.adapterType) },
      { label: 'Style', colClass: 'w-[9.25rem]', headerClass: GH, cellClass: GC,
        sortKey: 'adapterStyle', sortAccessor: (s) => s.adapterStyle, render: (s) => dash(s.adapterStyle) },
      { label: 'Dim 1', colClass: 'w-[6rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'adapterDim1', sortAccessor: (s) => adapterDimSortKey(s.adapterDim1), render: (s) => dash(s.adapterDim1) },
      { label: 'Dim 2', colClass: 'w-[6rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'adapterDim2', sortAccessor: (s) => adapterDimSortKey(s.adapterDim2), render: (s) => dash(s.adapterDim2) },
      { label: 'Material', colClass: 'w-[6.187rem]', headerClass: GH, cellClass: GC,
        sortKey: 'adapterMaterial', sortAccessor: (s) => s.adapterMaterial, render: (s) => dash(s.adapterMaterial) },
      { label: 'Color', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'adapterColor', sortAccessor: (s) => s.adapterColor, render: (s) => dash(s.adapterColor) },
    ],
  },
  // Hangers & Pipe Bands — 5 attrs. Heterogeneous category (hangers + pipe
  // hardware + straps) so Type leads. Widths PROVISIONAL; tune after a deploy
  // via the getBoundingClientRect measurement (size to the table's own widest).
  hanger: {
    categoryFilter: isHangerCategory,
    skuWidth: 'w-[11.75rem]',
    minWWithCost: 'min-w-[100.75rem]',
    minWNoCost: 'min-w-[93.75rem]',
    cols: [
      { label: 'Brand', colClass: 'w-[12.25rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'hangerBrand', sortAccessor: (s) => s.hangerBrand, render: (s) => dash(s.hangerBrand) },
      { label: 'Type', colClass: 'w-[6.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'hangerType', sortAccessor: (s) => s.hangerType, render: (s) => dash(s.hangerType) },
      { label: 'Style', colClass: 'w-[7.5rem]', headerClass: GH, cellClass: GC,
        sortKey: 'hangerStyle', sortAccessor: (s) => s.hangerStyle, render: (s) => dash(s.hangerStyle) },
      { label: 'Size', colClass: 'w-[5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'hangerSize', sortAccessor: (s) => hangerSizeSortKey(s.hangerSize), render: (s) => dash(s.hangerSize) },
      { label: 'Material', colClass: 'w-[6.5rem]', headerClass: GH, cellClass: GC,
        sortKey: 'hangerMaterial', sortAccessor: (s) => s.hangerMaterial, render: (s) => dash(s.hangerMaterial) },
      { label: 'Color', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'hangerColor', sortAccessor: (s) => s.hangerColor, render: (s) => dash(s.hangerColor) },
    ],
  },
  // Wedges & Shanks — 4 attrs. Heterogeneous (wedges + shanks) so Type leads; no
  // Style. Size mixes 5"/6" (wedge) and #N (shank). Widths PROVISIONAL; tune after
  // a deploy via getBoundingClientRect.
  wedgeShank: {
    categoryFilter: isWedgeShankCategory,
    skuWidth: 'w-[9.25rem]',
    minWWithCost: 'min-w-[94.25rem]',
    minWNoCost: 'min-w-[87.25rem]',
    cols: [
      { label: 'Brand', colClass: 'w-[9.25rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'wedgeShankBrand', sortAccessor: (s) => s.wedgeShankBrand, render: (s) => dash(s.wedgeShankBrand) },
      { label: 'Type', colClass: 'w-[14.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'wedgeShankType', sortAccessor: (s) => s.wedgeShankType, render: (s) => dash(s.wedgeShankType) },
      { label: 'Size', colClass: 'w-[4rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'wedgeShankSize', sortAccessor: (s) => wedgeShankSizeSortKey(s.wedgeShankSize), render: (s) => dash(s.wedgeShankSize) },
      { label: 'Material', colClass: 'w-[6rem]', headerClass: GH, cellClass: GC,
        sortKey: 'wedgeShankMaterial', sortAccessor: (s) => s.wedgeShankMaterial, render: (s) => dash(s.wedgeShankMaterial) },
      { label: 'Color', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'wedgeShankColor', sortAccessor: (s) => s.wedgeShankColor, render: (s) => dash(s.wedgeShankColor) },
    ],
  },
  // Screws Bolts Fasteners — 4 attrs. Heterogeneous (screw/bolt/rivet/nail) so Type
  // leads; no Material. Gauge + Length are the fastener spec. Widths PROVISIONAL;
  // tune after a deploy via getBoundingClientRect.
  fastener: {
    categoryFilter: isFastenerCategory,
    minWWithCost: 'min-w-[80.25rem]',
    minWNoCost: 'min-w-[73.25rem]',
    cols: [
      { label: 'Type', colClass: 'w-[5.5rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'fastenerType', sortAccessor: (s) => s.fastenerType, render: (s) => dash(s.fastenerType) },
      { label: 'Gauge', colClass: 'w-[5.5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'fastenerGauge', sortAccessor: (s) => fastenerGaugeSortKey(s.fastenerGauge), render: (s) => dash(s.fastenerGauge) },
      { label: 'Length', colClass: 'w-[5.5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'fastenerLength', sortAccessor: (s) => fastenerLengthSortKey(s.fastenerLength), render: (s) => dash(s.fastenerLength) },
      { label: 'Color', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'fastenerColor', sortAccessor: (s) => s.fastenerColor, render: (s) => dash(s.fastenerColor) },
    ],
  },
  // Sealer & Paint — 4 attrs. Non-metal category; Type carries the full sub-type
  // ("Construction Sealer" is the widest first-cell across all tables → wide Type
  // col). Color uses a LOCAL sealer-finish vocab. Widths PROVISIONAL; tune after
  // a deploy via getBoundingClientRect.
  sealerPaint: {
    categoryFilter: isSealerPaintCategory,
    minWWithCost: 'min-w-[85.5rem]',
    minWNoCost: 'min-w-[78.5rem]',
    cols: [
      { label: 'Type', colClass: 'w-[11rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'sealerPaintType', sortAccessor: (s) => s.sealerPaintType, render: (s) => dash(s.sealerPaintType) },
      { label: 'Brand', colClass: 'w-[6.5rem]', headerClass: GH, cellClass: GC,
        sortKey: 'sealerPaintBrand', sortAccessor: (s) => s.sealerPaintBrand, render: (s) => dash(s.sealerPaintBrand) },
      { label: 'Size', colClass: 'w-[5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'sealerPaintSize', sortAccessor: (s) => sealerPaintSizeSortKey(s.sealerPaintSize), render: (s) => dash(s.sealerPaintSize) },
      { label: 'Color', colClass: 'w-[9.75rem]', headerClass: GH, cellClass: GC,
        sortKey: 'sealerPaintColor', sortAccessor: (s) => s.sealerPaintColor, render: (s) => dash(s.sealerPaintColor) },
    ],
  },
  // Tools & Equipment — 2 attrs (thin grab-bag; honest, no padding). Column LABEL
  // is "Tool" (user preference) while the field is toolType. Tool col WIDE
  // ("Downspout Crimper"). Widths PROVISIONAL; tune after a deploy.
  tool: {
    categoryFilter: isToolCategory,
    minWWithCost: 'min-w-[74rem]',
    minWNoCost: 'min-w-[67rem]',
    cols: [
      { label: 'Tool', colClass: 'w-[10.5rem]', headerClass: GH, cellClass: GF, isFirstCell: true,
        sortKey: 'toolType', sortAccessor: (s) => s.toolType, render: (s) => dash(s.toolType) },
      { label: 'Size', colClass: 'w-[5rem]', headerClass: GH, cellClass: GCT,
        sortKey: 'toolSize', sortAccessor: (s) => toolSizeSortKey(s.toolSize), render: (s) => dash(s.toolSize) },
    ],
  },
} satisfies Record<string, AttributeTableDescriptor>;

export type AttrSet = keyof typeof ATTRIBUTE_TABLES;

// ── Catalog view: per-CATEGORY columns ────────────────────────────────────────
// The Catalog main-page view shows, after SKU · Description, a per-category column
// set (office spec 2026-06-19). Keyed by exact category name; categories absent
// here (Accessories / Insulation Accessories / Safety-PPE / Miscellaneous) show
// SKU · Description only. Unlike ATTRIBUTE_TABLES (one set per attribute family),
// this is per-category — the three fastener-split categories differ, and Gutter
// Coil uses the raw master-data columns (profile / face size / flat width / copper
// oz) rather than the derive-on-read attribute fields.
export interface CatalogColumn {
  label: string;
  render: (s: InventorySkuRow) => ReactNode;
  /** Right-align (numeric-ish values). */
  numeric?: boolean;
  /** Show a per-column faceted filter dropdown in the header. */
  filterable?: boolean;
  /** Full/unabbreviated name shown on header hover when `label` is abbreviated
   *  to fit a narrow column (e.g. label "Cu Oz" → title "Copper oz"). */
  title?: string;
}

const cdash = (v: string | number | null | undefined): ReactNode =>
  v === null || v === undefined || v === '' ? '—' : v;

export const CATALOG_CATEGORY_COLUMNS: Record<string, CatalogColumn[]> = {
  'Gutter Coil': [
    { label: 'Material', render: (s) => cdash(s.coilMaterial), filterable: true },
    { label: 'Color', render: (s) => cdash(s.coilColor), filterable: true },
    { label: 'Profile', render: (s) => cdash(s.coilProfile), filterable: true },
    { label: 'Temper', title: 'Coil temper', render: (s) => cdash(s.coilTemper), filterable: true },
    { label: 'Size', title: 'Face size', render: (s) => cdash(s.gutterFaceSize), filterable: true },
    { label: 'Width', title: 'Flat width', render: (s) => cdash(s.flatCoilWidth), filterable: true },
    { label: 'Thickness', render: (s) => cdash(s.coilThickness), numeric: true, filterable: true },
    { label: 'Cu Oz', title: 'Copper ounces', render: (s) => cdash(s.copperWeightOz), numeric: true, filterable: true },
  ],
  'Gutter Guards': [
    { label: 'Brand', render: (s) => cdash(s.guardBrand) },
    { label: 'Type', render: (s) => cdash(s.guardType) },
    { label: 'Material', render: (s) => cdash(s.guardMaterial) },
    { label: 'Color', render: (s) => cdash(s.guardColor) },
    { label: 'Length', render: (s) => cdash(s.guardLength), numeric: true },
    { label: 'Width', render: (s) => cdash(s.guardWidth), numeric: true },
  ],
  Downspouts: [
    { label: 'Style', render: (s) => cdash(s.downspoutStyle) },
    { label: 'Size', render: (s) => cdash(s.downspoutConnectorSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.downspoutMaterial) },
    { label: 'Color', render: (s) => cdash(s.downspoutColor) },
    { label: 'Length', render: (s) => cdash(s.downspoutLength), numeric: true },
  ],
  Elbows: [
    { label: 'Type', render: (s) => cdash(s.elbowType), numeric: true },
    { label: 'Style', render: (s) => cdash(s.elbowStyle) },
    { label: 'Size', render: (s) => cdash(s.elbowConnectorSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.elbowMaterial) },
    { label: 'Color', render: (s) => cdash(s.elbowColor) },
    { label: 'Angle', render: (s) => cdash(s.elbowAngle), numeric: true },
    { label: 'Offset', render: (s) => cdash(s.elbowOffset) },
  ],
  'End Caps': [
    { label: 'Style', render: (s) => cdash(s.endcapStyle) },
    { label: 'Size', render: (s) => cdash(s.endcapSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.endcapMaterial) },
    { label: 'Color', render: (s) => cdash(s.endcapColor) },
    { label: 'Side', title: 'Side (Left/Right)', render: (s) => cdash(s.endcapSide) },
  ],
  Miters: [
    { label: 'Type', render: (s) => cdash(s.miterType) },
    { label: 'Style', render: (s) => cdash(s.miterStyle) },
    { label: 'Size', render: (s) => cdash(s.miterSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.miterMaterial) },
    { label: 'Color', render: (s) => cdash(s.miterColor) },
    { label: 'Position', render: (s) => cdash(s.miterPosition) },
  ],
  'Adapters & Conductors': [
    { label: 'Brand', render: (s) => cdash(s.adapterBrand) },
    { label: 'Type', render: (s) => cdash(s.adapterType) },
    { label: 'Style', render: (s) => cdash(s.adapterStyle) },
    { label: 'Color', render: (s) => cdash(s.adapterColor) },
    { label: 'Dim 1', render: (s) => cdash(s.adapterDim1), numeric: true },
  ],
  Outlets: [
    { label: 'Brand', render: (s) => cdash(s.outletBrand) },
    { label: 'Type', render: (s) => cdash(s.outletType) },
    { label: 'Size', render: (s) => cdash(s.outletSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.outletMaterial) },
    { label: 'Color', render: (s) => cdash(s.outletColor) },
  ],
  'Hangers & Pipe Bands': [
    { label: 'Brand', render: (s) => cdash(s.hangerBrand) },
    { label: 'Type', render: (s) => cdash(s.hangerType) },
    { label: 'Style', render: (s) => cdash(s.hangerStyle) },
    { label: 'Size', render: (s) => cdash(s.hangerSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.hangerMaterial) },
    { label: 'Color', render: (s) => cdash(s.hangerColor) },
  ],
  'Wedges & Shanks': [
    { label: 'Brand', render: (s) => cdash(s.wedgeShankBrand) },
    { label: 'Type', render: (s) => cdash(s.wedgeShankType) },
    { label: 'Size', render: (s) => cdash(s.wedgeShankSize), numeric: true },
    { label: 'Material', render: (s) => cdash(s.wedgeShankMaterial) },
    { label: 'Color', render: (s) => cdash(s.wedgeShankColor) },
  ],
  Staples: [
    { label: 'Type', render: (s) => cdash(s.fastenerType) },
    { label: 'Length', render: (s) => cdash(s.fastenerLength), numeric: true },
  ],
  'Bolts & Anchors': [{ label: 'Type', render: (s) => cdash(s.fastenerType) }],
  'Sealer & Paint': [
    { label: 'Brand', render: (s) => cdash(s.sealerPaintBrand) },
    { label: 'Type', render: (s) => cdash(s.sealerPaintType) },
    { label: 'Size', render: (s) => cdash(s.sealerPaintSize), numeric: true },
    { label: 'Color', render: (s) => cdash(s.sealerPaintColor) },
  ],
  Fasteners: [
    { label: 'Type', render: (s) => cdash(s.fastenerType) },
    { label: 'Color', render: (s) => cdash(s.fastenerColor) },
    { label: 'Length', render: (s) => cdash(s.fastenerLength), numeric: true },
  ],
  'Tools & Equipment': [
    { label: 'Tool', render: (s) => cdash(s.toolType) },
    { label: 'Size', render: (s) => cdash(s.toolSize), numeric: true },
  ],
  Insulation: [
    { label: 'R-Value', render: (s) => cdash(s.rValue) },
    { label: 'Face', render: (s) => cdash(s.face) },
    { label: 'Width', render: (s) => (s.widthIn != null ? `${s.widthIn}"` : '—'), numeric: true },
    { label: 'Length', render: (s) => (s.lengthIn != null ? `${s.lengthIn}"` : '—'), numeric: true },
  ],
};

/** Per-category Catalog columns (after SKU · Description). Empty for catch-all
 *  categories (Accessories / Insulation Accessories / Safety-PPE / Miscellaneous). */
export function catalogColumnsForCategory(name: string): CatalogColumn[] {
  return CATALOG_CATEGORY_COLUMNS[name.trim()] ?? [];
}

// ── View-independent sort/filter resolution ───────────────────────────────────
// Sort + column-filter state are SHARED across the Catalog / Operational /
// Commercial views (so the order/filter persists when switching views). That only
// works if a sort/filter key resolves to the SAME value for a given row regardless
// of which view is active — hence these accessors live here (one source of truth),
// not per-view inside InventoryPage. Catalog attribute keys are resolved against the
// row's OWN category column set, so e.g. sorting by "Material" uses coilMaterial for
// a coil row and guardMaterial for a guard row.

/** Sort accessor for ANY column key, for ANY row. Fixed columns are resolved
 *  directly; catalog attribute keys via the row's category columns (with the same
 *  numeric coercion the catalog header sort used). Returns null when the key
 *  doesn't apply to the row (sorts those rows last). */
export function unifiedSortAccessor(s: InventorySkuRow, key: string): string | number | null {
  switch (key) {
    case 'code': return s.code;
    case 'Description': return s.name;
    case 'Cost': return s.costCents;
    case 'Factor': return s.conversionFactor;
    // Canonical (normalizeUom) so sort/filter/display all agree — and stored BX/BOX
    // collapse into one group. Must match the facet-option accessor in InventoryPage.
    case 'Purchase': return normalizeUom(s.purchaseUom);
    case 'Consume': return normalizeUom(s.consumptionUom);
    // Sort by the 3-state display label (OK / Needs / —), the same value the column
    // renders + the filter facet groups by — not the raw enum, which would scatter.
    case 'UoM status': return uomStatusDisplay(s.uomStatus).label;
    case 'On hand': return s.onHand;
    case 'Committed': return s.committedToQuotes;
    case 'Free': return s.effectivelyFree;
    case 'Reorder at': return s.reorderThreshold;
  }
  const col = catalogColumnsForCategory(s.categoryName).find((c) => c.label === key);
  if (!col) return null;
  const v = col.render(s);
  const str = typeof v === 'string' || typeof v === 'number' ? String(v) : '';
  if (str === '' || str === '—') return null;
  if (col.numeric) {
    const num = parseFloat(str.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(num) ? num : str;
  }
  return str;
}

/** Faceted-filter value accessor for any filterable column label, for any row.
 *  Same per-category resolution as the sort accessor so a filter set in one view
 *  applies in every view. */
export function unifiedFilterAccessor(s: InventorySkuRow, label: string): unknown {
  switch (label) {
    // Canonical so the facet matches the rows it lists (must match the option
    // accessor in InventoryPage); collapses any stored BX/BOX into one BOX facet.
    case 'Purchase': return normalizeUom(s.purchaseUom);
    case 'Consume': return normalizeUom(s.consumptionUom);
    // Facet + match on the 3-state display label so the dropdown lists OK / Needs / —
    // (not raw enum strings), and selecting one matches the rows it actually shows.
    case 'UoM status': return uomStatusDisplay(s.uomStatus).label;
  }
  const col = catalogColumnsForCategory(s.categoryName).find((c) => c.label === label);
  if (!col) return null;
  const v = col.render(s);
  return typeof v === 'string' || typeof v === 'number' ? v : null;
}

// SINGLE SOURCE for operational/commercial header display labels + tooltips: the
// Operational/Commercial <SortHeader>s render `label={sortKeyLabel(key)}` and the
// "Sorted by …" chip also reads sortKeyLabel(sort.key) — same function, same key, so
// header and chip can't drift. The KEYS on the left ('UoM status', 'Reorder at', …)
// are IDENTIFIERS reused across toggle()/sort.key/colFilterFor()/unifiedSortAccessor()
// — they are NOT display strings; do not Title-Case or otherwise rename them here.
// Only keys whose display differs from the key need an entry (else identity).
export const SORT_KEY_LABELS: Record<string, string> = {
  code: 'SKU',
  Purchase: 'BUY',
  Consume: 'SELL',
  'UoM status': 'UoM',
  'On hand': 'On Hand',
  Committed: 'Comm.',
  'Reorder at': 'Reorder',
};
export const sortKeyLabel = (key: string): string => SORT_KEY_LABELS[key] ?? key;

/** Full-name hover tooltip for a sort key (the abbreviated header's `title`).
 *  Undefined → SortHeader falls back to showing the label. */
export const SORT_KEY_TITLES: Record<string, string> = {
  Purchase: 'Purchase UoM',
  Consume: 'Consumption UoM',
  'UoM status': 'UoM status',
  Committed: 'Committed to quotes',
  'Reorder at': 'Reorder at threshold',
  Factor: 'Conversion factor',
};
export const sortKeyTitle = (key: string): string | undefined => SORT_KEY_TITLES[key];

// SINGLE SOURCE for the Operational "UoM" column display. Maps the stored
// `uomStatus` enum to one of three visual states. The six real values collapse to
// quiet-OK or amber-Needs; ANYTHING else (null, legacy, the stray
// PROPOSED_PACK_QTY_REVIEW worker state) → neutral fallback. It must NEVER yield
// the old "Review" string (the prior badge hardcoded it for every row).
//   - state drives the badge treatment (ok=quiet check, needs=amber pill, neutral=dash)
//   - label is the FILTER-FACET text (non-empty for every state so the dropdown
//     never groups rows under a blank facet)
//   - tooltip is the badge hover text
export type UomDisplayState = 'ok' | 'needs' | 'neutral';
export interface UomStatusDisplay {
  state: UomDisplayState;
  label: string;
  tooltip: string;
}
const UOM_OK_STATUSES = new Set([
  'OK_FACTOR_1',
  'OK_FROM_COVERAGE',
  'OK_PACK_QTY_SET',
  'OK_COIL_INDUSTRY_VALIDATED',
]);
export function uomStatusDisplay(status: string | null): UomStatusDisplay {
  if (status && UOM_OK_STATUSES.has(status)) {
    return { state: 'ok', label: 'OK', tooltip: status };
  }
  if (status === 'NEEDS_PACK_QTY') {
    return { state: 'needs', label: 'Needs', tooltip: 'Needs pack qty' };
  }
  if (status === 'NEEDS_COIL_WEIGHT_MISSING_DIMS') {
    return { state: 'needs', label: 'Needs', tooltip: 'Needs coil weight — missing dims' };
  }
  // null / unknown / legacy / PROPOSED_PACK_QTY_REVIEW → neutral, never "Review".
  return { state: 'neutral', label: '—', tooltip: status ?? 'No UoM status' };
}
